import { withSafeSuspense } from '@calendly/safe-suspense'
import { lazy } from 'react'

const autoLazy = (arg) => withSafeSuspense(lazy(arg))

// Breakdown
const EventDistributionByDuration = autoLazy(async () => ({ default: (await import('../charts/breakdown')).EventDistributionByDuration }))
const EventDistributionByUser = autoLazy(async () => ({ default: (await import('../charts/breakdown')).EventDistributionByUser }))
const EventTypeDistributionTable = autoLazy(async () => ({ default: (await import('../charts/breakdown')).EventTypeDistributionTable }))
const LatestOccurredMeetings = autoLazy(async () => ({ default: (await import('../charts/breakdown')).LatestOccurredMeetings }))
const PeopleWithFewestEvents = autoLazy(async () => ({ default: (await import('../charts/breakdown')).PeopleWithFewestEvents }))
const PeopleWithMostEvents = autoLazy(async () => ({ default: (await import('../charts/breakdown')).PeopleWithMostEvents }))
const PopularDaysAndTimesOfTheWeek = autoLazy(async () => ({ default: (await import('../charts/breakdown')).PopularDaysAndTimesOfTheWeek }))
const PopularEventTypes = autoLazy(async () => ({ default: (await import('../charts/breakdown')).PopularEventTypes }))
const UserDistributionTable = autoLazy(async () => ({ default: (await import('../charts/breakdown')).UserDistributionTable }))

// Interval
const EventVolumeTrend = autoLazy(async () => ({ default: (await import('../charts/interval')).EventVolumeTrend }))

// Single Value
const TotalBookedEvents = autoLazy(async () => ({ default: (await import('../charts/single_value')).TotalBookedEvents }))
const TotalCancelledEvents = autoLazy(async () => ({ default: (await import('../charts/single_value')).TotalCancelledEvents }))
const TotalOccurredEvents = autoLazy(async () => ({ default: (await import('../charts/single_value')).TotalOccurredEvents }))
const TotalRescheduledEvents = autoLazy(async () => ({ default: (await import('../charts/single_value')).TotalRescheduledEvents }))
const TotalUpcomingEvents = autoLazy(async () => ({ default: (await import('../charts/single_value')).TotalUpcomingEvents }))

// Empty State Charts
const SingleValueEmpty = autoLazy(async () => ({ default: (await import('../charts/single_value')).SingleValueEmpty }))
const BreakdownEmpty = autoLazy(async () => ({ default: (await import('../charts/breakdown')).BreakdownEmpty }))
const IntervalEmpty = autoLazy(async () => ({ default: (await import('../charts/interval')).IntervalEmpty }))

export const ReportingComponentRegistry = {
  eventDistributionByDuration: {
    component: EventDistributionByDuration
  },
  eventDistributionByUser: {
    component: EventDistributionByUser
  },
  eventTypeDistributionTable: {
    component: EventTypeDistributionTable
  },
  eventVolumeTrend: {
    component: EventVolumeTrend,
    tooltipFooterKey: 'completedDescription'
  },
  latestOccurredMeetings: {
    component: LatestOccurredMeetings
  },
  peopleWithFewestEvents: {
    component: PeopleWithFewestEvents,
    tooltipKey: 'fewestEvents'
  },
  peopleWithMostEvents: {
    component: PeopleWithMostEvents,
    tooltipKey: 'mostEvents'
  },
  popularDaysOfTheWeek: {
    component: PopularDaysAndTimesOfTheWeek,
    titleKey: 'popularTimes'
  },
  popularEventTypes: {
    component: PopularEventTypes,
    tooltipFooterKey: 'popularEventTypesFooter'
  },
  totalBookedEvents: {
    component: TotalBookedEvents
  },
  totalCancelledEvents: {
    component: TotalCancelledEvents,
  },
  totalOccurredEvents: {
    component: TotalOccurredEvents,
    tooltipFooterKey: 'completedDescription'
  },
  totalRescheduledEvents: {
    component: TotalRescheduledEvents,
  },
  totalUpcomingEvents: {
    component: TotalUpcomingEvents
  },
  userDistributionTable: {
    component: UserDistributionTable
  },
}

export const ReportingComponentEmptyRegistry = {
  eventDistributionByDuration: {
    component: () => <BreakdownEmpty reportKey="event_distribution_by_duration" />
  },
  eventDistributionByUser: {
    component: () => <BreakdownEmpty reportKey="event_distribution_by_user" />
  },
  eventVolumeTrend: {
    component: () => <IntervalEmpty reportKey="event_volume_trend" yTitleKey="event_volume" />
  },
  latestOccurredMeetings: {
    component: () => <BreakdownEmpty reportKey="latest_occurred_meetings" />
  },
  peopleWithFewestEvents: {
    component: () => <BreakdownEmpty reportKey="people_with_fewest_events" />
  },
  peopleWithMostEvents: {
    component: () => <BreakdownEmpty reportKey="people_with_most_events" />
  },
  popularDaysOfTheWeek: {
    component: () => <BreakdownEmpty reportKey="popular_times" />
  },
  popularEventTypes: {
    component: () => <BreakdownEmpty reportKey="popular_event_types" />
  },
  totalBookedEvents: {
    component: () => <SingleValueEmpty reportKey="total_booked_events" />
  },
  totalCancelledEvents: {
    component: () => <SingleValueEmpty reportKey="total_cancelled_events" />
  },
  totalOccurredEvents: {
    component: () => <SingleValueEmpty reportKey="total_occurred_events" />
  },
  totalRescheduledEvents: {
    component: () => <SingleValueEmpty reportKey="total_rescheduled_events" />
  },
  totalUpcomingEvents: {
    component: () => <SingleValueEmpty reportKey="total_upcoming_events" />
  },
}

export const EVENT_COMPONENTS = [
  'totalBookedEvents',
  'totalOccurredEvents',
  'totalRescheduledEvents',
  'totalCancelledEvents',
  'eventVolumeTrend',
  'eventDistributionByDuration',
  'popularEventTypes',
  'popularDaysOfTheWeek',
]

export const PERSON_COMPONENTS = [
  'peopleWithMostEvents',
  'peopleWithFewestEvents',
  'latestOccurredMeetings',
]

export const LEAD_REPORTS = [
  'totalBookedEvents',
  'totalOccurredEvents',
  'totalRescheduledEvents',
  'totalCancelledEvents',
]

export const UPCOMING_EVENT_REPORTS = [
  'totalUpcomingEvents',
  'popularEventTypes',
  'popularDaysOfTheWeek',
  'eventDistributionByDuration',
]

export const UPCOMING_WIDE_EVENT_REPORTS = [
  'eventDistributionByUser',
]

export const UPCOMING_BREAKDOWN_REPORTS = [
  'eventTypeDistributionTable',
  'userDistributionTable',
]

export const UPCOMING_REPORTS = [
  ...UPCOMING_EVENT_REPORTS,
  ...UPCOMING_WIDE_EVENT_REPORTS,
  ...UPCOMING_BREAKDOWN_REPORTS,
]

export const TABLE_REPORTS = [
  'latestOccurredMeetings',
  'popularEventTypes',
]

export const LINE_CHART_REPORTS = [
  'eventVolumeTrend',
  'popularDaysOfTheWeek',
]
